import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, X, RefreshCw, Sparkles, ShoppingBag, Heart, Loader2, ChevronRight, Trash2, ArrowLeft, Info, Share2, Pencil, PlusCircle, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useBuildLook, LOOK_SLOTS } from "../contexts/BuildLookContext";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent } from "../lib/analytics";
import SEO from "../components/SEO";
import { getDisplayName, getDisplayImage } from "../utils/variationDisplay";

const API = process.env.REACT_APP_BACKEND_URL;

// Exit Intent Hook
function useExitIntent(callback, enabled) {
  useEffect(() => {
    if (!enabled) return;
    
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        callback();
      }
    };
    
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [callback, enabled]);
}

// Slot Card Component
function SlotCard({ slotKey, slot, slotConfig, isHighlighted, onAdd, onReplace, onRemove }) {
  const isEmpty = !slot;
  
  return (
    <div 
      className={`relative bg-white border transition-all duration-300 h-full w-full overflow-hidden ${
        isHighlighted && isEmpty 
          ? "border-[#9A7B4F] ring-2 ring-[#9A7B4F]/20" 
          : "border-[#282828]/8 hover:border-[#282828]/15"
      }`}
      data-testid={`slot-${slotKey}`}
    >
      {isEmpty ? (
        // Empty state
        <button
          onClick={onAdd}
          className="w-full h-full flex flex-col items-center justify-center gap-3 group"
          data-testid={`add-${slotKey}-btn`}
        >
          <div className={`w-12 h-12 rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${
            isHighlighted ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/15 group-hover:border-[#9A7B4F]/50"
          }`}>
            <Plus className={`w-5 h-5 transition-colors ${
              isHighlighted ? "text-[#9A7B4F]" : "text-[#282828]/25 group-hover:text-[#9A7B4F]/50"
            }`} strokeWidth={1.5} />
          </div>
          <span className={`text-[11px] tracking-wider uppercase transition-colors ${
            isHighlighted ? "text-[#9A7B4F] font-medium" : "text-[#282828]/40 group-hover:text-[#282828]/60"
          }`}>
            {slotConfig.emptyLabel}
          </span>
          {isHighlighted && (
            <span className="absolute top-1 right-1 px-2 py-0.5 bg-[#9A7B4F] text-white text-[9px] tracking-wider uppercase rounded-full animate-pulse">
              Next
            </span>
          )}
        </button>
      ) : (
        // Filled state
        <div className="w-full h-full relative">
          <img 
            src={getDisplayImage(slot, slot) || "/placeholder.jpg"} 
            alt={getDisplayName(slot, slot)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-3">
            <p className="text-white text-[12px] font-medium truncate">{getDisplayName(slot, slot)}</p>
            <p className="text-white/70 text-[11px]">${(slot.rental_price || 0).toFixed(0)}</p>
          </div>
          {/* Actions */}
          <div className="absolute top-2 right-2 flex gap-1.5">
            <button
              onClick={onReplace}
              className="w-7 h-7 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-sm transition-colors"
              title="Replace"
              data-testid={`replace-${slotKey}-btn`}
            >
              <RefreshCw className="w-3.5 h-3.5 text-[#282828]" strokeWidth={1.5} />
            </button>
            <button
              onClick={onRemove}
              className="w-7 h-7 bg-white/90 hover:bg-red-50 rounded-full flex items-center justify-center shadow-sm transition-colors"
              title="Remove"
              data-testid={`remove-${slotKey}-btn`}
            >
              <X className="w-3.5 h-3.5 text-[#282828] hover:text-red-500" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// AI Recommendation Card
function AIRecommendationCard({ product, onAddToLook, onViewMore, rentalPeriod }) {
  const [adding, setAdding] = useState(false);
  
  const handleAdd = async () => {
    setAdding(true);
    await onAddToLook(product);
    setAdding(false);
  };

  const displayPrice = rentalPeriod === 8
    ? (product.price_8_day || product.rental_price || product.price_4_day || 0)
    : (product.price_4_day || product.rental_price || product.price || 0);
  
  return (
    <div className="flex-shrink-0 w-[140px] bg-white border border-[#282828]/8 rounded-lg overflow-hidden" data-testid={`ai-pick-${product.id}`}>
      <div className="aspect-[3/4] relative">
        <img 
          src={product.images?.[0] || product.image || "/placeholder.jpg"} 
          alt={product.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-2">
        <p className="text-[10px] text-[#282828] font-medium truncate">{product.name}</p>
        <p className="text-[11px] text-[#9A7B4F] font-semibold mt-0.5">${displayPrice.toFixed(0)}</p>
        <div className="flex gap-1.5 mt-1.5">
          <button
            onClick={handleAdd}
            disabled={adding}
            className="flex-1 py-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[8px] tracking-wider uppercase rounded transition-colors disabled:opacity-50"
            data-testid={`ai-add-${product.id}`}
          >
            {adding ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : "Add"}
          </button>
          <button
            onClick={() => onViewMore(product)}
            className="px-2 py-1 border border-[#282828]/15 text-[#282828]/60 text-[8px] tracking-wider uppercase rounded hover:border-[#282828]/30 transition-colors"
            data-testid={`ai-view-${product.id}`}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BuildYourLookPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer } = useCustomerAuth();
  const {
    isActive,
    slots,
    eventDate,
    rentalPeriod,
    deliveryMethod: lookDeliveryMethod,
    itemCount,
    discountPercent,
    originalTotal,
    discountAmount,
    finalPrice,
    totalBond,
    progress,
    progressMessage,
    nextEmptySlot,
    aiRecommendations,
    loadingAI,
    selectSlot,
    addToLook,
    removeFromSlot,
    replaceSlot,
    resetLook,
    fetchAIRecommendations,
    rentEntireLook,
    setIsActive,
    lockDeliveryMethod,
    loadLookFromData,
    lookMode,
    setLookMode,
    setCartLookItemIds,
  } = useBuildLook();
  const { fetchCart } = useCart();
  
  const [showExitModal, setShowExitModal] = useState(false);
  const [savingLook, setSavingLook] = useState(false);
  const [variationPopup, setVariationPopup] = useState(null); // { product, selectedOptions: [] }
  const [rentingLook, setRentingLook] = useState(false);
  const [popularity, setPopularity] = useState(null);
  const [postcodeInput, setPostcodeInput] = useState("");
  const [postcodeStatus, setPostcodeStatus] = useState(null); // null | "checking" | "eligible" | "ineligible"
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [cartLookItems, setCartLookItems] = useState([]); // look items found in cart
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuRef = useRef(null);

  // Close header menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setShowHeaderMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Check if cart has look items on mount
  const hasCheckedCart = useRef(false);
  useEffect(() => {
    if (hasCheckedCart.current) return;
    hasCheckedCart.current = true;

    const rebuildId = searchParams.get("rebuildSavedLook");
    if (rebuildId) {
      // Re-build from saved look — handled separately below
      return;
    }

    const checkCartForLooks = async () => {
      try {
        const sessionId = localStorage.getItem("mahra_session_id") || "";
        if (!sessionId) return;
        const res = await axios.get(`${API}/api/cart`, { headers: { "x-session-id": sessionId } });
        const groups = res.data.groups || [];
        const allItems = groups.flatMap(g => [g.main, ...g.addons]);
        const lookItems = allItems.filter(i => i.is_look_item);
        if (lookItems.length > 0) {
          setCartLookItems(lookItems);
          setShowChoiceModal(true);
        }
      } catch (e) {
        console.error("Failed to check cart for looks:", e);
      }
    };
    checkCartForLooks();
  }, [searchParams]);

  // Handle rebuild from saved look
  useEffect(() => {
    const rebuildId = searchParams.get("rebuildSavedLook");
    if (!rebuildId) return;
    searchParams.delete("rebuildSavedLook");
    setSearchParams(searchParams, { replace: true });

    const loadSavedLook = async () => {
      try {
        const customerId = customer?.customer_id || customer?.id;
        if (!customerId) {
          toast.error("Please sign in to re-build a saved look");
          navigate("/login?redirect=/build-your-look");
          return;
        }
        const res = await axios.get(`${API}/api/my-styled-looks/${rebuildId}`, {
          headers: { "x-customer-id": customerId }
        });
        const look = res.data;
        loadLookFromData(
          look.slots || {},
          look.event_date || null,
          look.rental_period || null,
          look.delivery_method || "pickup"
        );
        toast.success("Saved look loaded — make your changes!");
      } catch (e) {
        console.error("Failed to load saved look:", e);
        toast.error("Failed to load saved look");
      }
    };
    loadSavedLook();
  }, [searchParams, customer]);

  // Handle choice modal actions
  const handleChoiceEditCurrent = () => {
    // Load cart look items into the editor
    const slotsData = { outfit: null, jewellery: null, clutch: null, accessory: null };
    let date = null, period = null, method = "pickup";

    for (const item of cartLookItems) {
      const cat = (item.outfit_category || "").toLowerCase();
      let slotKey = "accessory";
      if (cat.includes("lehenga") || cat.includes("saree") || cat.includes("indo") || cat.includes("anarkali") || cat.includes("sharara") || cat.includes("for-her")) {
        slotKey = "outfit";
      } else if (cat.includes("jewellery") || cat.includes("jewelry")) {
        slotKey = "jewellery";
      } else if (cat.includes("clutch") || cat.includes("potli")) {
        slotKey = "clutch";
      }
      if (!slotsData[slotKey]) {
        slotsData[slotKey] = {
          id: item.outfit_id,
          name: item.outfit_name,
          images: [item.outfit_image],
          image: item.outfit_image,
          rental_price: item.rental_price || 0,
          bond_amount: item.bond_amount || 0,
          category: item.outfit_category || "",
          rental_option_id: item.rental_option_id || null,
          rental_option_name: item.rental_option_name || null,
        };
      }
      if (!date) date = item.event_date;
      if (!period) period = item.duration;
      method = item.delivery_method || "pickup";
    }

    setCartLookItemIds(cartLookItems.map(i => i.id));
    setLookMode("edit_current");
    loadLookFromData(slotsData, date, period, method);
    setShowChoiceModal(false);
    toast.info("Editing your current look");
  };

  const handleChoiceStartFresh = () => {
    setCartLookItemIds(cartLookItems.map(i => i.id));
    setLookMode("start_fresh");
    resetLook();
    setIsActive(true);
    setShowChoiceModal(false);
    toast.info("Starting fresh — your old look will be replaced");
  };

  const handleChoiceBuildAnother = () => {
    setCartLookItemIds([]);
    setLookMode("build_another");
    resetLook();
    setIsActive(true);
    setShowChoiceModal(false);
    toast.info("Building another look — your existing look stays in cart");
  };

  const handleChoiceCancel = () => {
    setShowChoiceModal(false);
    navigate(-1);
  };
  
  // Reset delivery method if price drops below $175 after postcode approval
  useEffect(() => {
    if (postcodeStatus === "eligible" && lookDeliveryMethod === "delivery" && finalPrice < 175) {
      lockDeliveryMethod("pickup");
      setPostcodeStatus(null);
      setPostcodeInput("");
      toast.info("Order dropped below $175 — switched back to Pick Up");
    }
  }, [finalPrice, postcodeStatus, lookDeliveryMethod, lockDeliveryMethod]);
  
  // Mark as active on mount and track page view
  useEffect(() => {
    setIsActive(true);
    trackEvent("build_look_started");
  }, [setIsActive]);
  
  // Fetch AI recommendations when outfit changes
  useEffect(() => {
    if (slots.outfit) {
      fetchAIRecommendations();
    }
  }, [slots.outfit?.id, fetchAIRecommendations]);
  
  // Fetch popularity when items change
  useEffect(() => {
    const fetchPopularity = async () => {
      const productIds = Object.values(slots).filter(Boolean).map(s => s.id);
      if (productIds.length < 2) {
        setPopularity(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/api/look-popularity/${productIds.join(",")}`);
        setPopularity(res.data);
      } catch (err) {
        console.error("Failed to fetch popularity:", err);
      }
    };
    fetchPopularity();
  }, [slots]);
  
  // Exit intent
  useExitIntent(() => {
    if (itemCount > 0 && itemCount < 4) {
      setShowExitModal(true);
    }
  }, itemCount > 0 && itemCount < 4);

  // Auto-add product from URL param (when user clicks "Build Your Own Look" from PDP)
  useEffect(() => {
    const addProductId = searchParams.get("addProduct");
    if (!addProductId) return;
    
    // Remove param from URL to prevent re-adding on re-render
    searchParams.delete("addProduct");
    setSearchParams(searchParams, { replace: true });
    
    // Fetch product and determine slot
    const autoAddProduct = async () => {
      try {
        const res = await axios.get(`${API}/api/outfits/${addProductId}`);
        const product = res.data;
        if (!product || !product.id) return;
        
        // Determine slot based on category
        const cat = (product.category || "").toLowerCase();
        let slotKey = "accessory"; // default
        
        if (cat.includes("lehenga") || cat.includes("saree") || cat.includes("indo") || cat.includes("anarkali") || cat.includes("sharara") || cat.includes("for-her") || cat.includes("outfit")) {
          slotKey = "outfit";
        } else if (cat.includes("jewellery") || cat.includes("jewelry")) {
          slotKey = "jewellery";
        } else if (cat.includes("clutch") || cat.includes("potli")) {
          slotKey = "clutch";
        }
        
        // Only add if slot is empty
        if (!slots[slotKey]) {
          // Enrich product with rental_price for pricing panel
          const enrichedProduct = {
            ...product,
            rental_price: product.rental_price || product.price_4_day || product.price || 0,
          };
          addToLook(enrichedProduct, slotKey);
        } else {
          toast.info(`${LOOK_SLOTS[slotKey].label} slot is already filled`);
        }
      } catch (err) {
        console.error("Failed to auto-add product:", err);
      }
    };
    
    autoAddProduct();
  }, [searchParams]);
  
  // Handle rent entire look
  const handleRentLook = async () => {
    setRentingLook(true);
    trackEvent("rent_entire_look_clicked", "", { 
      item_count: itemCount, 
      discount_percent: discountPercent,
      final_price: finalPrice,
      look_mode: lookMode,
    });
    const success = await rentEntireLook();
    setRentingLook(false);
    if (success) {
      fetchCart(); // refresh cart count
      navigate("/cart");
    }
  };
  
  // Handle save look
  const handleSaveLook = async () => {
    if (itemCount < 2) {
      toast.error("Please add at least 2 items to save your look");
      return;
    }
    
    const token = localStorage.getItem("mahra_session_token");
    if (!customer || !token) {
      // Persist look payload for post-login auto-save
      sessionStorage.setItem("mahra_pending_save_look", JSON.stringify({
        slots, event_date: eventDate, rental_period: rentalPeriod,
        discount_percent: discountPercent, original_total: originalTotal,
        final_price: finalPrice, total_bond: totalBond, delivery_method: lookDeliveryMethod,
      }));
      toast.info("Sign in to save your look");
      navigate("/login?redirect=/build-your-look");
      return;
    }

    setSavingLook(true);
    try {
      await saveLookAPI();
      toast.success("Look saved to your account!");
      trackEvent("look_saved", "", { item_count: itemCount, discount_percent: discountPercent, final_price: finalPrice });
    } catch (err) {
      console.error("Failed to save look:", err);
      toast.error("Failed to save look. Please try again.");
    } finally {
      setSavingLook(false);
    }
  };

  const saveLookAPI = async (payload) => {
    const data = payload || {
      slots, event_date: eventDate, rental_period: rentalPeriod,
      discount_percent: discountPercent, original_total: originalTotal,
      final_price: finalPrice, total_bond: totalBond, delivery_method: lookDeliveryMethod,
    };
    const sessionId = localStorage.getItem("mahra_session_id") || "";
    const customerId = customer?.customer_id || "";
    return axios.post(`${API}/api/my-styled-looks`, data, {
      headers: {
        "x-session-id": sessionId, "x-customer-id": customerId,
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("mahra_session_token")}`,
      }
    });
  };

  // Auto-save pending look after login redirect
  useEffect(() => {
    const pending = sessionStorage.getItem("mahra_pending_save_look");
    const token = localStorage.getItem("mahra_session_token");
    if (pending && token && customer?.customer_id) {
      sessionStorage.removeItem("mahra_pending_save_look");
      const payload = JSON.parse(pending);
      setSavingLook(true);
      saveLookAPI(payload)
        .then(() => {
          toast.success("Look saved to your account!");
          trackEvent("look_saved", "", { item_count: itemCount });
        })
        .catch(() => toast.error("Failed to save look. Please try again."))
        .finally(() => setSavingLook(false));
    }
  }, [customer]);
  
  // Handle quick add from AI recommendations - DIRECT add without navigation
  const handleQuickAdd = async (product) => {
    // If product has variations, show popup first
    if (product.rental_options?.length > 0) {
      setVariationPopup({ product, selectedOptions: [] });
      return;
    }
    addProductToLook(product, null);
  };

  // Add product to look (after variation selection or directly)
  const addProductToLook = (product, variationSelections) => {
    // Determine which slot to add to based on product category
    let targetSlot = null;
    
    const productCategory = (product.category || product.categories?.[0] || "").toLowerCase();
    if (productCategory.includes("jewellery") && !slots.jewellery) {
      targetSlot = "jewellery";
    } else if ((productCategory.includes("clutch") || productCategory.includes("potli")) && !slots.clutch) {
      targetSlot = "clutch";
    } else if (!slots.accessory && !productCategory.includes("jewellery") && !productCategory.includes("clutch") && !productCategory.includes("potli")) {
      targetSlot = "accessory";
    } else {
      if (!slots.jewellery) targetSlot = "jewellery";
      else if (!slots.clutch) targetSlot = "clutch";
      else if (!slots.accessory) targetSlot = "accessory";
    }
    
    if (!targetSlot || targetSlot === "outfit") {
      toast.error("All accessory slots are filled");
      return;
    }
    
    // Check if the exact same product+variation is already in the look.
    // Different variations of the same product are allowed (see BuildLookContext).
    const incomingVariation = variationSelections
      ? variationSelections.map(o => o.id).join(",")
      : null;
    const existingSlot = Object.entries(slots).find(([_, item]) => {
      if (!item || item.id !== product.id) return false;
      return (item?.rental_option_id || null) === incomingVariation;
    });
    if (existingSlot) {
      toast.error(
        incomingVariation
          ? "This variation is already in your look. Pick a different one to add another option."
          : "This item is already in your look"
      );
      return;
    }
    
    const rprice = rentalPeriod === 8
      ? (product.price_8_day || product.rental_price || product.price_4_day || 0)
      : (product.price_4_day || product.rental_price || product.price || 0);
    const productForLook = {
      id: product.id,
      name: product.name,
      images: product.images || [product.image],
      image: product.images?.[0] || product.image,
      rental_price: rprice,
      bond_amount: product.bond_amount || product.deposit || 0,
      category: product.category,
      ...(variationSelections ? {
        rental_option_id: variationSelections.map(o => o.id).join(","),
        rental_option_name: variationSelections.map(o => o.name).join(" + "),
      } : {}),
    };
    
    addToLook(productForLook, targetSlot);
    trackEvent("accessory_added", product.id, { slot: targetSlot, source: "ai_picks" });
  };
  
  // Handle view more
  const handleViewMore = (product) => {
    const targetSlot = nextEmptySlot !== "outfit" ? nextEmptySlot : "accessory";
    navigate(`/outfit/${product.id}?buildLook=true&slot=${targetSlot}`);
  };
  
  // Days until event
  const daysUntilEvent = eventDate 
    ? Math.ceil((new Date(eventDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
      <Navbar />
      
      <div className="flex-1 max-w-[1280px] w-full mx-auto px-4 lg:px-8 py-6 lg:py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[11px] text-[#676664]/50 mb-6">
          <Link to="/" className="hover:text-[#676664]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#282828]">Build Your Own Look</span>
        </div>
        
        {/* Header */}
        <SEO
          title="Build Your Look - Mix & Match Designer Outfits"
          description="Build your own styled look. Mix and match designer Indian outfits, jewellery & accessories. Save up to 20% when you bundle. Rent in Melbourne."
          path="/build-your-look"
          breadcrumbs={[{ name: "Home", url: "/" }, { name: "Build Your Look" }]}
          noindex
        />
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Build Your Own Look
            </h1>
            <p className="text-[13px] text-[#676664]/60 mt-1">
              {lookMode === "edit_current" ? "Editing your current look" 
                : lookMode === "build_another" ? "Building an additional look"
                : "Curate your perfect outfit and save up to 20%"}
            </p>
          </div>
          <div className="relative" ref={headerMenuRef}>
            {cartLookItems.length > 0 ? (
              <>
                <button
                  onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                  className="text-[11px] text-[#676664]/50 hover:text-[#676664] uppercase tracking-wider flex items-center gap-1.5 px-3 py-2 border border-[#282828]/10 rounded-lg hover:border-[#282828]/20 transition-colors"
                  data-testid="look-options-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Options
                </button>
                {showHeaderMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#282828]/10 rounded-lg shadow-lg z-30 w-56 py-1" data-testid="look-options-menu">
                    <button
                      onClick={() => { setShowHeaderMenu(false); handleChoiceEditCurrent(); }}
                      className="w-full text-left px-4 py-2.5 text-[11px] text-[#282828] hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors"
                      data-testid="option-edit-current"
                    >
                      <Pencil className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                      <div>
                        <span className="font-medium block">Edit Current Look</span>
                        <span className="text-[#676664]/50 text-[10px]">Modify your look in cart</span>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowHeaderMenu(false); handleChoiceStartFresh(); }}
                      className="w-full text-left px-4 py-2.5 text-[11px] text-[#282828] hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors"
                      data-testid="option-start-fresh"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                      <div>
                        <span className="font-medium block">Start Fresh</span>
                        <span className="text-[#676664]/50 text-[10px]">Replace with a new look</span>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowHeaderMenu(false); handleChoiceBuildAnother(); }}
                      className="w-full text-left px-4 py-2.5 text-[11px] text-[#282828] hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors"
                      data-testid="option-build-another"
                    >
                      <PlusCircle className="w-3.5 h-3.5 text-[#9A7B4F]" strokeWidth={1.5} />
                      <div>
                        <span className="font-medium block">Build Another Look</span>
                        <span className="text-[#676664]/50 text-[10px]">Keep current, add a new one</span>
                      </div>
                    </button>
                    <div className="border-t border-[#282828]/5 mt-1 pt-1">
                      <button
                        onClick={() => { setShowHeaderMenu(false); resetLook(); }}
                        className="w-full text-left px-4 py-2.5 text-[11px] text-[#676664]/60 hover:bg-[#FAF8F0] flex items-center gap-2.5 transition-colors"
                        data-testid="option-start-over"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span>Start Over</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : itemCount > 0 ? (
              <button
                onClick={resetLook}
                className="text-[11px] text-[#676664]/50 hover:text-[#676664] uppercase tracking-wider flex items-center gap-1.5"
                data-testid="start-over-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Start Over
              </button>
            ) : null}
          </div>
        </div>
        
        {/* Main Layout - Compact like Shop the Look */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_320px] gap-6 lg:gap-8 lg:items-stretch items-start">
          
          {/* Left: Slot Grid - Shop the Look exact dimensions */}
          <div>
            {/* Desktop Layout: Outfit (2.5fr), 3 accessories (1fr each ~194px tall) */}
            <div className="hidden md:grid gap-[6px] overflow-hidden" style={{ gridTemplateColumns: 'minmax(0, 2.5fr) minmax(0, 1fr)', height: '600px' }}>
              {/* Outfit - Large (665px x 600px) */}
              <SlotCard
                slotKey="outfit"
                slot={slots.outfit}
                slotConfig={LOOK_SLOTS.outfit}
                isHighlighted={nextEmptySlot === "outfit"}
                onAdd={() => selectSlot("outfit")}
                onReplace={() => replaceSlot("outfit")}
                onRemove={() => removeFromSlot("outfit")}
              />
              
              {/* Accessories - Stacked (266px x 195px each) */}
              <div className="flex flex-col gap-[6px]">
                <div style={{ height: '194px' }}>
                  <SlotCard
                    slotKey="jewellery"
                    slot={slots.jewellery}
                    slotConfig={LOOK_SLOTS.jewellery}
                    isHighlighted={nextEmptySlot === "jewellery"}
                    onAdd={() => selectSlot("jewellery")}
                    onReplace={() => replaceSlot("jewellery")}
                    onRemove={() => removeFromSlot("jewellery")}
                  />
                </div>
                <div style={{ height: '194px' }}>
                  <SlotCard
                    slotKey="clutch"
                    slot={slots.clutch}
                    slotConfig={LOOK_SLOTS.clutch}
                    isHighlighted={nextEmptySlot === "clutch"}
                    onAdd={() => selectSlot("clutch")}
                    onReplace={() => replaceSlot("clutch")}
                    onRemove={() => removeFromSlot("clutch")}
                  />
                </div>
                <div style={{ height: '194px' }}>
                  <SlotCard
                    slotKey="accessory"
                    slot={slots.accessory}
                    slotConfig={LOOK_SLOTS.accessory}
                    isHighlighted={nextEmptySlot === "accessory"}
                    onAdd={() => selectSlot("accessory")}
                    onReplace={() => replaceSlot("accessory")}
                    onRemove={() => removeFromSlot("accessory")}
                  />
                </div>
              </div>
            </div>
            
            {/* Mobile Layout: Vertical stack */}
            <div className="md:hidden space-y-4">
              <SlotCard
                slotKey="outfit"
                slot={slots.outfit}
                slotConfig={LOOK_SLOTS.outfit}
                isHighlighted={nextEmptySlot === "outfit"}
                onAdd={() => selectSlot("outfit")}
                onReplace={() => replaceSlot("outfit")}
                onRemove={() => removeFromSlot("outfit")}
              />
              <div className="grid grid-cols-3 gap-3">
                <SlotCard
                  slotKey="jewellery"
                  slot={slots.jewellery}
                  slotConfig={LOOK_SLOTS.jewellery}
                  isHighlighted={nextEmptySlot === "jewellery"}
                  onAdd={() => selectSlot("jewellery")}
                  onReplace={() => replaceSlot("jewellery")}
                  onRemove={() => removeFromSlot("jewellery")}
                />
                <SlotCard
                  slotKey="clutch"
                  slot={slots.clutch}
                  slotConfig={LOOK_SLOTS.clutch}
                  isHighlighted={nextEmptySlot === "clutch"}
                  onAdd={() => selectSlot("clutch")}
                  onReplace={() => replaceSlot("clutch")}
                  onRemove={() => removeFromSlot("clutch")}
                />
                <SlotCard
                  slotKey="accessory"
                  slot={slots.accessory}
                  slotConfig={LOOK_SLOTS.accessory}
                  isHighlighted={nextEmptySlot === "accessory"}
                  onAdd={() => selectSlot("accessory")}
                  onReplace={() => replaceSlot("accessory")}
                  onRemove={() => removeFromSlot("accessory")}
                />
              </div>
            </div>
          </div>
          
          {/* Right: Pricing Panel */}
          <div className="bg-white border border-[#282828]/8 rounded-lg p-5 lg:h-full lg:max-h-[600px] lg:overflow-y-auto lg:flex lg:flex-col" data-testid="pricing-panel">
            <h2 className="text-[16px] font-medium text-[#282828] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Build Your Look
            </h2>
            
            {/* Progress Bar */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-[#676664]/60">Complete Your Look</span>
                <span className="text-[11px] text-[#9A7B4F] font-medium">{progress}%</span>
              </div>
              <div className="h-2 bg-[#FAF8F0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#9A7B4F] to-[#C5A572] rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-[11px] text-[#676664]/60 mt-2">{progressMessage}</p>
            </div>
            
            {/* Discount Badge */}
            {discountPercent > 0 && (
              <div className="mb-4 px-3 py-2 bg-[#FDF6EC] border border-[#E8D5B5] rounded-lg flex items-center gap-2" data-testid="discount-badge">
                <Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                <span className="text-[12px] text-[#9A7B4F] font-medium">{itemCount} Items • Save {discountPercent}%</span>
              </div>
            )}
            
            {/* Popularity Indicator */}
            {popularity?.message && (
              <div className="mb-4 px-3 py-2 bg-[#F0F6F2] border border-[#B8C9BD] rounded-lg flex items-center gap-2" data-testid="popularity-badge">
                <span className="text-[12px]">{popularity.is_trending ? "🔥" : "✨"}</span>
                <span className="text-[12px] text-[#2D3A30] font-medium">{popularity.message}</span>
              </div>
            )}
            
            {/* Event Date Display */}
            {eventDate && (
              <div className="mb-4 text-[11px] text-[#676664]/70">
                <span className="font-medium">Event:</span> {new Date(eventDate).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
                {daysUntilEvent !== null && daysUntilEvent > 0 && (
                  <span className="ml-2 text-[#9A7B4F]">({daysUntilEvent} days away)</span>
                )}
              </div>
            )}
            
            {/* Pricing Breakdown */}
            <div className="space-y-2 py-4 border-t border-[#282828]/8 lg:flex-1 lg:flex lg:flex-col lg:justify-end">
              <div className="flex justify-between text-[12px]">
                <span className="text-[#676664]">Items Selected</span>
                <span className="text-[#282828] font-medium">{itemCount}</span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-[#676664]">Original Price</span>
                <span className="text-[#282828]">${originalTotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-[12px]">
                  <span className="text-[#9A7B4F]">Bundle Discount ({discountPercent}%)</span>
                  <span className="text-[#9A7B4F] font-medium">-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              {/* Delivery — Pickup shows FREE, delivery eligible shows postcode check */}
              {itemCount > 0 && (() => {
                const isFreeDeliveryEligible = postcodeStatus === "eligible" && lookDeliveryMethod === "delivery" && finalPrice >= 175;
                const showPostcodePrompt = lookDeliveryMethod === "pickup" && finalPrice >= 175 && postcodeStatus !== "eligible";
                const deliveryFee = lookDeliveryMethod !== "pickup" && finalPrice < 175 ? 15 : 0;
                return (
                <div className="space-y-2">
                  {showPostcodePrompt ? (
                    /* Pickup selected but order qualifies — show postcode check prompt */
                    <div className="bg-[#F0F7F4] border border-[#2D6A4F]/15 rounded-lg p-3">
                      <p className="text-[11px] text-[#2D6A4F] font-medium mb-2">
                        Great news! Your order may qualify for free delivery.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          value={postcodeInput}
                          onChange={(e) => { setPostcodeInput(e.target.value.replace(/\D/g, "")); setPostcodeStatus(null); }}
                          placeholder="Enter postcode"
                          className="flex-1 px-3 py-1.5 border border-[#2D6A4F]/20 rounded text-[12px] text-[#282828] bg-white focus:outline-none focus:border-[#2D6A4F]/50"
                          data-testid="delivery-postcode-input"
                        />
                        <button
                          onClick={async () => {
                            if (postcodeInput.length !== 4) return;
                            setPostcodeStatus("checking");
                            try {
                              const res = await axios.get(`${API}/api/service-area/check?postcode=${postcodeInput}`);
                              if (res.data.is_serviced) {
                                setPostcodeStatus("eligible");
                                lockDeliveryMethod("delivery");
                                toast.success("Free delivery unlocked!");
                              } else {
                                setPostcodeStatus("ineligible");
                              }
                            } catch { setPostcodeStatus("ineligible"); }
                          }}
                          disabled={postcodeInput.length !== 4 || postcodeStatus === "checking"}
                          className="px-3 py-1.5 bg-[#2D6A4F] text-white text-[10px] tracking-wider uppercase rounded hover:bg-[#245A41] transition-colors disabled:opacity-50"
                          data-testid="check-postcode-btn"
                        >
                          {postcodeStatus === "checking" ? "..." : "Check"}
                        </button>
                      </div>
                      {postcodeStatus === "ineligible" && (
                        <p className="text-[10px] text-[#9A7B4F] mt-2">
                          Unfortunately, your area is outside our current delivery range. No worries — free pick up is still available!
                        </p>
                      )}
                    </div>
                  ) : isFreeDeliveryEligible ? (
                    /* Free delivery confirmed */
                    <div className="flex justify-between text-[12px] bg-[#F0F7F4] border border-[#2D6A4F]/15 rounded-lg px-3 py-2">
                      <span className="text-[#2D6A4F] font-medium">Delivery</span>
                      <span className="text-[#2D6A4F] font-bold">FREE</span>
                    </div>
                  ) : lookDeliveryMethod === "pickup" ? (
                    /* Pickup (under $175 or default) */
                    <div className="flex justify-between text-[12px]">
                      <span className="text-[#676664]">Delivery</span>
                      <span className="text-[#2D6A4F] font-medium">FREE (Pick Up)</span>
                    </div>
                  ) : (
                    /* Delivery selected, under $175 */
                    <div className="flex justify-between text-[12px]">
                      <span className="text-[#676664]">Delivery</span>
                      <span className="text-[#282828]">$15.00</span>
                    </div>
                  )}
                </div>
                );
              })()}
              <div className="flex justify-between text-[14px] pt-2 border-t border-[#282828]/8">
                <span className="text-[#282828] font-medium">Final Price</span>
                <span className="text-[#9A7B4F] font-bold text-[18px]" data-testid="final-price-display">
                  ${(lookDeliveryMethod !== "pickup" && finalPrice < 175 ? finalPrice + 15 : finalPrice).toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Primary CTA */}
            <Button
              onClick={handleRentLook}
              disabled={itemCount < 2 || rentingLook || !eventDate}
              className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-4 text-[11px] tracking-[0.15em] uppercase"
              data-testid="rent-look-btn"
            >
              {rentingLook ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : lookMode === "edit_current" ? (
                `Update Look — $${(lookDeliveryMethod !== "pickup" && finalPrice < 175 ? finalPrice + 15 : finalPrice).toFixed(0)}`
              ) : lookMode === "build_another" ? (
                `Add Look to Cart — $${(lookDeliveryMethod !== "pickup" && finalPrice < 175 ? finalPrice + 15 : finalPrice).toFixed(0)}`
              ) : (
                `Rent Entire Look — $${(lookDeliveryMethod !== "pickup" && finalPrice < 175 ? finalPrice + 15 : finalPrice).toFixed(0)}`
              )}
            </Button>
            
            {/* Bond Info */}
            {totalBond > 0 && (
              <p className="text-[10px] text-[#676664]/50 text-center mt-2 flex items-center justify-center gap-1">
                <Info className="w-3 h-3" />
                + ${totalBond.toFixed(0)} refundable bond (returned after rental)
              </p>
            )}
            
            {/* Secondary Actions */}
            <div className="flex gap-3 mt-4">
              <Button
                onClick={handleSaveLook}
                disabled={itemCount < 2 || savingLook}
                variant="outline"
                className="flex-1 border-[#282828]/15 text-[#282828]/70 py-2.5 text-[10px] tracking-wider uppercase"
                data-testid="save-look-btn"
              >
                {savingLook ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                  <>
                    <Heart className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                    Save Look
                  </>
                )}
              </Button>
              <Button
                onClick={resetLook}
                variant="outline"
                className="flex-1 border-[#282828]/15 text-[#282828]/70 py-2.5 text-[10px] tracking-wider uppercase"
                data-testid="start-over-panel-btn"
              >
                Start Over
              </Button>
            </div>
            
            {/* Social Sharing */}
            {itemCount >= 2 && (
              <div className="mt-4 pt-4 border-t border-[#282828]/8">
                <p className="text-[10px] text-[#676664]/50 text-center mb-2">Share your styled look</p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => {
                      const text = `Check out my styled look on Mahra! ${itemCount} pieces, ${discountPercent}% off. 🛍️`;
                      const url = window.location.href;
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
                    }}
                    className="w-8 h-8 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 flex items-center justify-center transition-colors"
                    title="Share on Facebook"
                  >
                    <svg className="w-4 h-4 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </button>
                  <button
                    onClick={() => {
                      const text = `Check out my styled look on Mahra! ${itemCount} pieces, ${discountPercent}% off. ✨`;
                      const url = window.location.href;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
                    }}
                    className="w-8 h-8 rounded-full bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 flex items-center justify-center transition-colors"
                    title="Share on Twitter"
                  >
                    <svg className="w-4 h-4 text-[#1DA1F2]" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                  </button>
                  <button
                    onClick={() => {
                      const text = `Check out my styled look on Mahra! ${itemCount} pieces, ${discountPercent}% off.`;
                      const url = window.location.href;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                    }}
                    className="w-8 h-8 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 flex items-center justify-center transition-colors"
                    title="Share on WhatsApp"
                  >
                    <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        toast.success("Link copied to clipboard!");
                      } catch {
                        toast.error("Failed to copy link");
                      }
                    }}
                    className="w-8 h-8 rounded-full bg-[#282828]/5 hover:bg-[#282828]/10 flex items-center justify-center transition-colors"
                    title="Copy Link"
                  >
                    <Share2 className="w-4 h-4 text-[#282828]/60" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Helper text */}
            {itemCount < 2 && (
              <p className="text-[10px] text-[#676664]/40 text-center mt-3">
                Add at least 2 items to rent your look
              </p>
            )}
          </div>
        </div>
        
        {/* AI Stylist Picks — Below the main grid, full width */}
        {slots.outfit && (
          <div className="mt-10 overflow-hidden" data-testid="ai-stylist-picks">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <h3 className="text-[14px] font-medium text-[#282828]">Mahra AI Stylist Picks</h3>
            </div>
            
            {loadingAI ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
              </div>
            ) : aiRecommendations.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
                {aiRecommendations.map((product) => (
                  <AIRecommendationCard
                    key={product.id}
                    product={product}
                    onAddToLook={handleQuickAdd}
                    onViewMore={handleViewMore}
                    rentalPeriod={rentalPeriod}
                  />
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-[#676664]/50 py-4">
                Browse categories to find matching accessories
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Variation Selection Popup */}
      {variationPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="variation-popup">
          <div className="bg-white rounded-t-xl sm:rounded-lg w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#282828]/8 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-[16px] font-medium text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {variationPopup.product.name}
                </h3>
                <p className="text-[11px] text-[#676664] mt-0.5">
                  {variationPopup.product.variation_selection_mode === "multiple"
                    ? "Select the items you'd like to include"
                    : "Choose your preferred option"}
                </p>
              </div>
              <button
                onClick={() => setVariationPopup(null)}
                className="w-8 h-8 rounded-full hover:bg-[#282828]/5 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-[#282828]/40" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-2.5">
              {variationPopup.product.rental_options.map((option) => {
                const isMulti = variationPopup.product.variation_selection_mode === "multiple";
                const isSelected = variationPopup.selectedOptions.some(o => o.id === option.id);
                const optionPrice = rentalPeriod <= 4
                  ? (option.price_4_day || option.price_per_day || 0)
                  : (option.price_8_day || option.price_4_day || option.price_per_day || 0);

                return (
                  <div
                    key={option.id}
                    onClick={() => {
                      setVariationPopup(prev => {
                        const selected = prev.selectedOptions;
                        if (isMulti) {
                          return {
                            ...prev,
                            selectedOptions: isSelected
                              ? selected.filter(o => o.id !== option.id)
                              : [...selected, option],
                          };
                        }
                        return { ...prev, selectedOptions: [option] };
                      });
                    }}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border rounded-lg transition-all ${
                      isSelected
                        ? "border-[#9A7B4F]/50 bg-[#F5F0E6]"
                        : "border-[#282828]/8 hover:border-[#282828]/15"
                    }`}
                    data-testid={`variation-option-${option.id}`}
                  >
                    {isMulti ? (
                      <div className={`w-5 h-5 rounded border-[1.5px] flex-shrink-0 flex items-center justify-center ${
                        isSelected ? "border-[#9A7B4F] bg-[#9A7B4F]" : "border-[#282828]/20"
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M2 6l3 3 5-5" />
                          </svg>
                        )}
                      </div>
                    ) : (
                      <div className={`w-5 h-5 rounded-full border-[1.5px] flex-shrink-0 flex items-center justify-center ${
                        isSelected ? "border-[#9A7B4F]" : "border-[#282828]/20"
                      }`}>
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#9A7B4F]" />}
                      </div>
                    )}
                    {option.image && (
                      <img src={option.image} alt={option.name} className="w-10 h-10 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-[#282828] font-medium truncate">{option.name}</p>
                      {option.description && (
                        <p className="text-[11px] text-[#676664] truncate">{option.description}</p>
                      )}
                    </div>
                    {optionPrice > 0 && (
                      <span className="text-[12px] text-[#9A7B4F] font-medium flex-shrink-0">${optionPrice}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-[#282828]/8 px-5 py-4">
              <Button
                onClick={() => {
                  if (variationPopup.selectedOptions.length === 0) {
                    toast.error("Please select at least one option");
                    return;
                  }
                  addProductToLook(variationPopup.product, variationPopup.selectedOptions);
                  setVariationPopup(null);
                }}
                disabled={variationPopup.selectedOptions.length === 0}
                className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-3 text-[11px] tracking-[0.15em] uppercase rounded-none disabled:opacity-40"
                data-testid="confirm-variation-btn"
              >
                Add to Look
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Exit Intent Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="exit-intent-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
              <h3 className="text-[18px] font-medium text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Don't leave your look behind!
              </h3>
            </div>
            <p className="text-[13px] text-[#676664] mb-2">
              You're <span className="text-[#9A7B4F] font-semibold">{100 - progress}% away</span> from completing your styled look.
            </p>
            <p className="text-[12px] text-[#676664]/80 mb-4">
              {itemCount === 1 && "Add just 1 more item to unlock 10% off!"}
              {itemCount === 2 && "Add 1 more item for 15% off, or 2 more for 20% off!"}
              {itemCount === 3 && "You're so close! Add 1 more accessory to unlock the full 20% discount."}
            </p>
            {/* Progress bar */}
            <div className="h-2 bg-[#FAF8F0] rounded-full overflow-hidden mb-5">
              <div 
                className="h-full bg-gradient-to-r from-[#9A7B4F] to-[#C5A572] rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowExitModal(false)}
                className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-3"
              >
                Continue Building
              </Button>
              <Button
                onClick={() => {
                  setShowExitModal(false);
                  navigate("/");
                }}
                variant="outline"
                className="flex-1 border-[#282828]/15 text-[#676664] py-3"
              >
                Leave Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Choice Modal - shown when user has look in cart */}
      {showChoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="look-choice-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-[20px] text-[#282828] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              You have a look in your cart
            </h3>
            <p className="text-[12px] text-[#676664]/60 mb-5">
              {cartLookItems.length} item{cartLookItems.length > 1 ? "s" : ""} from a styled look
            </p>

            <div className="space-y-2.5">
              <button
                onClick={handleChoiceEditCurrent}
                className="w-full text-left p-4 border border-[#9A7B4F]/20 rounded-lg hover:border-[#9A7B4F]/50 hover:bg-[#FDF6EC] transition-all group"
                data-testid="choice-edit-current"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Pencil className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-[13px] text-[#282828] font-medium block">Edit Current Look</span>
                    <span className="text-[11px] text-[#676664]/60 mt-0.5 block">Modify items, dates, or delivery for your existing look</span>
                  </div>
                </div>
              </button>

              <button
                onClick={handleChoiceStartFresh}
                className="w-full text-left p-4 border border-[#282828]/8 rounded-lg hover:border-[#9A7B4F]/30 hover:bg-[#FDF6EC] transition-all group"
                data-testid="choice-start-fresh"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#282828]/5 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#9A7B4F]/10">
                    <Sparkles className="w-4 h-4 text-[#676664] group-hover:text-[#9A7B4F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-[13px] text-[#282828] font-medium block">Start Fresh</span>
                    <span className="text-[11px] text-[#676664]/60 mt-0.5 block">Replace your current look with a brand new one</span>
                  </div>
                </div>
              </button>

              <button
                onClick={handleChoiceBuildAnother}
                className="w-full text-left p-4 border border-[#282828]/8 rounded-lg hover:border-[#9A7B4F]/30 hover:bg-[#FDF6EC] transition-all group"
                data-testid="choice-build-another"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#282828]/5 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#9A7B4F]/10">
                    <PlusCircle className="w-4 h-4 text-[#676664] group-hover:text-[#9A7B4F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-[13px] text-[#282828] font-medium block">Build Another Look</span>
                    <span className="text-[11px] text-[#676664]/60 mt-0.5 block">Keep your current look and build an additional one</span>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={handleChoiceCancel}
              className="w-full text-center text-[11px] text-[#676664]/50 hover:text-[#676664] mt-4 py-2 uppercase tracking-wider"
              data-testid="choice-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}
